import { ActionIcon, Badge, Divider, Group, Indicator, Stack, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCopy, IconTrash } from '@tabler/icons-react';
import { PlanType } from 'api/models';
import { usePackage } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useHistory } from 'react-router';
import { isEmpty } from 'remeda';
import { type LightTemplate, type UsedByResource, useDeleteTemplate } from './api';
import { DuplicateTemplateModal } from './DuplicateTemplateModal';
import { CHANNEL_CONFIGS } from './TemplateEditor/channels';
import classes from './TemplateRow.module.css';

type TemplateRowProps = {
  template: LightTemplate;
};

export const TemplateRow = ({ template }: TemplateRowProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { deleteTemplate } = useDeleteTemplate();
  const canCreateTemplate = usePackage().can_create_template;

  const goToEdit = () => history.push(`/settings/templates/${template.id}`);

  const openDuplicateModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!canCreateTemplate) return;

    modals.open({
      title: t(i18nKeys.DUPLICATE),
      centered: true,
      children: <DuplicateTemplateModal template={template} onClose={modals.closeAll} />,
    });
  };

  const confirmDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    modals.openConfirmModal({
      title: t(i18nKeys.DELETE_TEMPLATE),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.AYS.DELETE_TEMPLATE)}</Text>,
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red', leftSection: <IconTrash size={16} stroke={1.5} /> },
      onConfirm: () => deleteTemplate({ id: template.id }),
    });
  };

  const goToUsedByResource = (resource: UsedByResource) => (event: React.MouseEvent) => {
    event.stopPropagation();
    const path =
      template.planType === PlanType.payment
        ? `/settings/workflows/${PlanType.payment}/edit/${resource.id}`
        : `/settings/workflows/recovery-plans/edit/${resource.id}`;
    history.push(path);
  };

  return (
    <Group
      className={classes.row}
      justify="space-between"
      wrap="nowrap"
      px="md"
      py="sm"
      onClick={goToEdit}
    >
      <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm">{template.name}</Text>
        <TemplateChannelBadges channels={template.channels} />
      </Stack>
      <Stack gap={8} align="flex-end">
        <Text size="sm" c="dimmed" ta="right">
          {t(i18nKeys.TEMPLATE.LAST_UPDATED, { date: template.updatedAt.format('L') })}
        </Text>
        <Group gap={6} justify="flex-end" maw={320}>
          {template.usedBy.map(resource => (
            <Badge
              key={resource.id}
              size="sm"
              radius="sm"
              variant="light"
              tt="none"
              style={{ cursor: 'pointer' }}
              onClick={goToUsedByResource(resource)}
            >
              {resource.name}
            </Badge>
          ))}
        </Group>
      </Stack>
      <Divider orientation="vertical" />
      <Group gap="lg" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Tooltip label={t(i18nKeys.NOT_INCLUDED)} disabled={canCreateTemplate}>
            <ActionIcon
              variant="light"
              aria-label={t(i18nKeys.DUPLICATE)}
              data-disabled={!canCreateTemplate || undefined}
              onClick={openDuplicateModal}
            >
              <IconCopy size={18} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={template.noDeletionReason} disabled={!template.noDeletionReason}>
            <Indicator
              label="?"
              size={16}
              position="top-end"
              color="gray"
              disabled={!template.noDeletionReason}
              withBorder
              styles={{ indicator: { paddingInline: 0, fontSize: 10 } }}
            >
              <ActionIcon
                variant="light"
                color="red"
                aria-label={t(i18nKeys.DELETE)}
                disabled={template.noDeletionReason !== null}
                onClick={confirmDelete}
              >
                <IconTrash size={18} stroke={1.5} />
              </ActionIcon>
            </Indicator>
          </Tooltip>
        </Group>
      </Group>
    </Group>
  );
};

type TemplateChannelBadgesProps = {
  channels: LightTemplate['channels'];
};

const TemplateChannelBadges = ({ channels }: TemplateChannelBadgesProps) => {
  const { t } = useTranslation();

  if (isEmpty(channels)) {
    return (
      <Badge size="sm" radius="sm" variant="light" color="gray" tt="none">
        {t(i18nKeys.TEMPLATE.NO_CHANNELS)}
      </Badge>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      {CHANNEL_CONFIGS.filter(({ key }) => channels.includes(key)).map(
        ({ key, labelKey, Icon }) => (
          <Badge
            key={key}
            size="sm"
            radius="sm"
            variant="light"
            color={key === 'email' ? 'blue' : key === 'letter' ? 'green' : 'gray'}
            tt="none"
            leftSection={<Icon size={12} stroke={1.5} />}
          >
            {t(labelKey)}
          </Badge>
        ),
      )}
    </Group>
  );
};
